import React, {useState} from 'react';
import {Form} from 'react-final-form';
import {MessageDescriptor, useIntl} from 'react-intl';

import useReturnAuthorizationMgr from 'coreHooks/useReturnAuthorizationMgr';
import validationRules from 'coreUtils/validationRules';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Field from 'brandComponents/Form/Subcomponents/Field';
import Grid from 'brandComponents/Grid';
import Typography from 'brandComponents/Typography';
import VerifyForm from 'brandComponents/VerifyForm';

import messages from './messages';

import styles from './ReturnAuthorizationLoginForm.module.scss';

const ReturnAuthorizationLoginForm = ({onReturnOrderSelected}) => {
	const {managerRepository} = useGeneralProvider();
	const intl = useIntl();
	const returnAuthorizationMgr = useReturnAuthorizationMgr();
	const [orderErrorKey, setOrderErrorKey] = useState<MessageDescriptor | null>(null);

	const handleSubmitForm = async (values) => {
		try {
			const returnOrder = await returnAuthorizationMgr.getGuestReturnAuthorizationOrder(values);

			if (returnOrder.isReturnable()) {
				setOrderErrorKey(null);
				onReturnOrderSelected(returnOrder);
			} else {
				setOrderErrorKey(messages.orderNotEligible);
			}
		} catch (e) {
			setOrderErrorKey(messages.orderError);
		}
	};

	return (
		<div className={styles['return-login-form']}>
			<Grid fluid classNames={styles['container-fluid']}>
				<Grid.Row>
					<Grid.Column md={12}>
						<Typography classNames={styles['return-login-form__title']} variant="h3">
							{intl.formatMessage(messages.title)}
						</Typography>
					</Grid.Column>
					<Grid.Column md={12}>
						<Typography classNames={styles['return-login-form__description']} variant="p">
							{intl.formatMessage(messages.description2)}
						</Typography>
					</Grid.Column>
				</Grid.Row>
				<Form
					onSubmit={(values) => handleSubmitForm(values)}
					render={({handleSubmit, submitting, hasValidationErrors}) => (
						<VerifyForm classNames={styles['return-login-form__form']} onSubmit={handleSubmit}>
							<Grid.Row>
								<Grid.Column md={12}>
									<Field
										name="orderNo"
										type="text"
										id="orderNo"
										component="input"
										label={intl.formatMessage(messages.orderNoPlaceholder)}
										validation={[
											{
												rule: validationRules.isRequired,
												errorMessage: intl.formatMessage(messages.orderRequiredError),
											},
										]}
									/>
								</Grid.Column>
								<Grid.Column md={12}>
									<Field
										name="email"
										type="email"
										id="email"
										component="input"
										label={intl.formatMessage(messages.emailPlaceholder)}
										validation={[
											{
												rule: validationRules.isRequired,
												errorMessage: intl.formatMessage(messages.emailRequiredError),
											},
											{
												rule: validationRules.isEmail,
												errorMessage: intl.formatMessage(messages.emailInvalidError),
											},
										]}
									/>
								</Grid.Column>
							</Grid.Row>
							{orderErrorKey && (
								<span className={styles['return-login-form__error']}>
									{intl.formatMessage(orderErrorKey)}
								</span>
							)}
							<Typography variant="p" classNames={styles['return-login-form__comment']}>
								{intl.formatMessage(messages.mandatoryFields)}
							</Typography>
							<Button
								classNames={styles['return-login-form__btn']}
								disabled={submitting || hasValidationErrors}
								type="submit"
								fixed
							>
								{intl.formatMessage(messages.submitButton)}
							</Button>
						</VerifyForm>
					)}
				/>
				<div className={styles['return-login-form__login-description__wrap']}>
					<Typography classNames={styles['return-login-form__login-description']} variant="p">
						{intl.formatMessage(messages.description1)}
					</Typography>
					<Button
						classNames={styles['return-login-form__btn']}
						onClick={() => managerRepository.getFlyoutsMgr().openFlyout('login', true)}
						fixed
					>
						{intl.formatMessage(messages.loginButton)}
					</Button>
				</div>
				<Typography classNames={styles['return-login-form__note']} variant="p">
					{intl.formatMessage(messages.description3, {h4: (chunk) => <span>{chunk}</span>})}
				</Typography>
			</Grid>
		</div>
	);
};

export default ReturnAuthorizationLoginForm;
